import { useState } from 'react';
import { MoreVertical, Archive, ArchiveRestore, Copy, Loader2, Pencil } from 'lucide-react';
import { useRouter } from '@tanstack/react-router';
import { Button } from '@/components/atoms/button';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/atoms/alert-dialog';
import { OverflowMenuContent, RowOverflowMenuTrigger } from '@/components/molecules';
import { useArchiveItem } from '@/hooks/pricebook/useArchiveItem';
import { useRestoreItem } from '@/hooks/pricebook/useRestoreItem';

export interface PricebookItemActionsProps {
  id: string;
  name: string;
  variant: 'row' | 'header';
  /**
   * NOT-105: when true, the item is already archived → show Restore in place
   * of Archive. Defaults to false. Pass `item.status === 'ARCHIVED'` from the
   * detail page; row contexts default to ACTIVE because archived items drop
   * out of the default list view.
   */
  isArchived?: boolean;
  /** Header: override default edit navigation. Row: ignored (row Edit navigates to detail/edit). */
  onEdit?: () => void;
  /** Navigate to list after archive. Defaults to true for header. */
  navigateAfterArchive?: boolean;
}

export function PricebookItemActions({
  id,
  name,
  variant,
  isArchived = false,
  onEdit,
  navigateAfterArchive,
}: PricebookItemActionsProps) {
  const router = useRouter();
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const archiveMutation = useArchiveItem();
  const restoreMutation = useRestoreItem();

  const shouldNavigateAfterArchive = navigateAfterArchive ?? variant === 'header';

  const handleEdit = () => {
    if (onEdit) {
      onEdit();
      return;
    }
    void router.navigate({
      to: '/dashboard/pricebook/$id/edit',
      params: { id },
      search: (prev) => prev as never,
    });
  };

  const handleArchiveConfirm = () => {
    archiveMutation.mutate(id, {
      onSuccess: () => {
        setShowArchiveConfirm(false);
        if (shouldNavigateAfterArchive) {
          void router.navigate({ to: '/dashboard/pricebook', search: (prev) => prev as never });
        }
      },
    });
  };

  const handleRestore = () => {
    restoreMutation.mutate(id);
  };

  const handleDuplicate = () => {
    void router.navigate({
      to: '/dashboard/pricebook/new',
      search: (prev) => ({ ...prev, duplicate: id }) as never,
    });
  };

  if (variant === 'header') {
    return (
      <>
        <div className="hidden items-center gap-1.5 md:flex">
          <Button variant="outline" size="icon-sm" title="Edit" onClick={handleEdit}>
            <Pencil size={14} />
          </Button>
          <Button variant="outline" size="icon-sm" title="Duplicate" onClick={handleDuplicate}>
            <Copy size={14} />
          </Button>
          {isArchived ? (
            <Button
              variant="outline"
              size="icon-sm"
              title="Restore"
              className="text-emerald-600 border-emerald-600/40 hover:bg-emerald-600/10"
              onClick={handleRestore}
              disabled={restoreMutation.isPending}
            >
              {restoreMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ArchiveRestore size={14} />
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="icon-sm"
              title="Archive"
              className="text-destructive border-destructive/40 hover:bg-destructive/10"
              onClick={() => setShowArchiveConfirm(true)}
            >
              <Archive size={14} />
            </Button>
          )}
        </div>
        <div className="md:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon-sm" title="Actions" aria-label="Actions">
                <MoreVertical size={14} />
              </Button>
            </DropdownMenuTrigger>
            <OverflowMenuContent>
              <DropdownMenuItem onSelect={handleEdit}>
                <Pencil className="mr-2 size-3.5" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={handleDuplicate}>
                <Copy className="mr-2 size-3.5" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isArchived ? (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleRestore();
                  }}
                >
                  <ArchiveRestore className="mr-2 size-3.5" />
                  Restore
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    setShowArchiveConfirm(true);
                  }}
                  className="text-destructive focus:text-destructive"
                >
                  <Archive className="mr-2 size-3.5" />
                  Archive
                </DropdownMenuItem>
              )}
            </OverflowMenuContent>
          </DropdownMenu>
        </div>
        <ArchiveDialog
          open={showArchiveConfirm}
          onOpenChange={setShowArchiveConfirm}
          onConfirm={handleArchiveConfirm}
          name={name}
          isPending={archiveMutation.isPending}
        />
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <RowOverflowMenuTrigger />
        </DropdownMenuTrigger>
        <OverflowMenuContent>
          <DropdownMenuItem onSelect={handleEdit}>
            <Pencil className="mr-2 size-3.5" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleDuplicate}>
            <Copy className="mr-2 size-3.5" />
            Duplicate
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {isArchived ? (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                handleRestore();
              }}
            >
              <ArchiveRestore className="mr-2 size-3.5" />
              Restore
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                setShowArchiveConfirm(true);
              }}
              className="text-destructive focus:text-destructive"
            >
              <Archive className="mr-2 size-3.5" />
              Archive
            </DropdownMenuItem>
          )}
        </OverflowMenuContent>
      </DropdownMenu>
      <ArchiveDialog
        open={showArchiveConfirm}
        onOpenChange={setShowArchiveConfirm}
        onConfirm={handleArchiveConfirm}
        name={name}
        isPending={archiveMutation.isPending}
      />
    </>
  );
}

function ArchiveDialog({
  open,
  onOpenChange,
  onConfirm,
  name,
  isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: () => void;
  name: string;
  isPending: boolean;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Archive item?</AlertDialogTitle>
          <AlertDialogDescription>
            Archive "{name}"? It will drop out of default lists and search, but existing services,
            estimates, and jobs that reference it stay intact. You can restore it later.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isPending ? (
              <Loader2 className="mr-1 size-3.5 animate-spin" />
            ) : (
              <Archive className="mr-1 size-3.5" />
            )}
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
